// 바닥 드롭 이름표(드롭 식별 회차, 대표님 2026-10-27 "줍는 게 뭔지 식별이 안 된다"): 드롭 위에 작은 DOM 이름표 — 종류 그림 + 이름(등급색) + 종류 태그(장비·재료·보석).
// 규칙(많이 떨어져도 화면이 어지럽지 않게):
// - 보이는 조건: 영웅 반경 LOOT_LABEL.near 안, 또는 떨어진 지 LOOT_LABEL.freshTicks 틱(약 4초) 안, 세트·전설은 LOOT_LABEL.bigNear 안이면 계속.
// - 동시에 최대 LOOT_LABEL.max개: 영웅과 가까운 순(세트·전설은 거리 보너스)으로 뽑는다. 나머지는 빛기둥·고리만.
// - 겹침 정리: 화면에서 겹치면 위로 밀어 올린다(가까운 것이 제자리).
// 가리지 않기: 보이는 HUD 패널(panelRects: 초상·상태·목표 카드·미니맵·마을 카드)과 조작 영역(HudAvoid: 연계 안내 줄·자동 버튼·스킬 바·터치 버튼) 밑으로 들어간 이름표는 옆으로 비킨다(avoidShift),
//   비켜도 겹치면 그 이름표는 접는다. 화면 위쪽 TOP_KEEP px(상단 HUD 띠)·아래쪽 height × 0.86 밖으로는 나가지 않는다.
// 이름표 DOM은 생성 때 max개를 만들어 돌려 쓰고, 프레임마다 거리·화면 좌표만 갱신한다(좌표가 바뀔 때만 style 기록). 내용은 드롭이 바뀔 때만 다시 쓴다.
import type * as THREE from 'three/webgpu';
import { combatScene, type GameView } from '../shared/types';
import { assetUrl } from '../pwa/assetMap';
import { TagPosCache } from '../vfx/tagCache';
import { avoidShift } from './env/label';
import { HudAvoid } from './hudAvoid';
import type { ScreenRect } from '../shared/panelRects';
import { KIND_ICON, KIND_TAG_COLOR, gearDropInfo, gemDropInfo, stoneDropInfo, type DropInfo } from '../ui/dropInfo';
import type { GearItem } from '../shared/gear';

/** 화면 위쪽에서 이름표를 두지 않는 높이(px, 상단 HUD 띠: 체력·초상·적/보스 체력) */
const TOP_KEEP = 52;

export const LOOT_LABEL = {
  /** 동시에 띄우는 이름표 수 상한 */
  max: 8,
  /** 영웅 반경(m): 안이면 항상 */
  near: 6.5,
  /** 세트·전설은 이 반경(m) 안이면 계속 */
  bigNear: 13,
  /** 떨어진 직후 이 틱(약 4초) 동안은 거리와 관계없이(최대 freshFar m 안) */
  freshTicks: 240,
  freshFar: 16,
  /** 세로 겹침 판정 높이(px, 배율 전)와 밀어 올리는 간격 */
  rowH: 22,
  /** 가로 겹침 판정에 쓰는 이름표 폭 추정(px, 배율 전) */
  maxW: 170,
} as const;
/** 등급별 거리 보너스(m²): 값이 클수록 같은 거리에서 먼저 뽑힌다. 장비 등급 번호 순, 강화석·보석은 0 */
const GRADE_BONUS = [0, 4, 14, 60, 110] as const;
/** 이미 이름표 칸을 차지한 드롭에 주는 점수 보너스(m²): 상위 max 경계에서 두 드롭이 비슷할 때 이름표가 번갈아 바뀌며 깜빡이는 것을 막는다 */
const HOLD_BONUS = 6;

const NO_RECTS: readonly ScreenRect[] = [];

/** 상자 [x0,x1]×[y0,y1]이 예약 사각형과 4px 넘게 겹치는가(경계에 닿는 정도는 겹침으로 보지 않는다) */
export function overlaps(x0: number, x1: number, y0: number, y1: number, rects: readonly ScreenRect[]): boolean {
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (Math.min(x1, r.x + r.w) - Math.max(x0, r.x) > 4 && Math.min(y1, r.y + r.h) - Math.max(y0, r.y) > 4) return true;
  }
  return false;
}

const FONT_NAME = '800 12px system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif';
const FONT_TAG = '900 9px system-ui,"Malgun Gothic",sans-serif';
const widthCache = new Map<string, number>();
let measureCtx: CanvasRenderingContext2D | null | undefined;
function textW(font: string, text: string, fallbackPx: number): number {
  if (measureCtx === undefined) {
    try { measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null; } catch { measureCtx = null; }
  }
  if (!measureCtx) return text.length * fallbackPx;
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}
/**
 * 이름표 폭 추정(px, 배율 전): 안쪽 여백·테두리 + 그림 18 + 간격 + 이름 + 간격 + 태그. 이름·태그 쌍마다 한 번만 재서 캐시한다(레이아웃 읽기 없음).
 * 겹침 정리에만 쓰는 값이라 몇 px 어긋나도 된다
 */
export function labelWidth(name: string, tag: string): number {
  const key = name + '\u0001' + tag;
  let w = widthCache.get(key);
  if (w === undefined) {
    w = Math.min(LOOT_LABEL.maxW, Math.round(2 + 6 + 2 + 18 + 4 + textW(FONT_NAME, name, 12) + 4 + textW(FONT_TAG, tag, 9) + 6 + 2));
    if (widthCache.size > 512) widthCache.clear();
    widthCache.set(key, w);
  }
  return w;
}

interface Entry {
  readonly root: HTMLDivElement;
  readonly box: HTMLDivElement;
  readonly img: HTMLImageElement;
  readonly name: HTMLSpanElement;
  readonly tag: HTMLSpanElement;
  readonly pos: TagPosCache;
  /** 지금 가리키는 드롭 칸(-1 = 비어 있음)과 내용 비교용 값 */
  slot: number;
  item: GearItem | null;
  kind: number; grade: number; aux: number; tier: number;
  icons: readonly string[];
  iconTry: number;
  /** 측정한 폭(px, 배율 전) */
  w: number;
  shown: boolean;
}

export class LootLabels {
  private readonly layer: HTMLDivElement;
  private readonly entries: Entry[] = [];
  /** 이번 프레임에 고른 드롭 칸과 점수(작을수록 먼저). 길이 max 고정 */
  private readonly selIdx = new Int32Array(LOOT_LABEL.max);
  private readonly selScore = new Float32Array(LOOT_LABEL.max);
  /** 이번 프레임 화면 좌표(겹침 정리용) */
  private readonly sx = new Float32Array(LOOT_LABEL.max);
  private readonly sy = new Float32Array(LOOT_LABEL.max);
  private readonly ent = new Int32Array(LOOT_LABEL.max);
  /** QA: 이번 프레임에 띄운 수 */
  shownCount = 0;
  private any = false;
  private readonly hudAvoid = new HudAvoid();

  constructor(fxLayer: HTMLElement) {
    this.layer = document.createElement('div');
    this.layer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;display:none;';
    this.layer.dataset.testid = 'loot-labels';
    fxLayer.appendChild(this.layer);
    for (let i = 0; i < LOOT_LABEL.max; i++) this.entries.push(this.makeEntry());
  }

  private makeEntry(): Entry {
    const root = document.createElement('div');
    root.style.cssText = 'position:absolute;left:0;top:0;display:none;align-items:center;gap:4px;box-sizing:border-box;max-width:' + LOOT_LABEL.maxW + 'px;padding:1px 6px 1px 2px;border-radius:7px;' +
      'white-space:nowrap;pointer-events:none;will-change:transform;transform-origin:0 0;font:800 12px system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif;color:#fff;' +
      'background:rgba(8,12,28,.74);border:1px solid #fff;text-shadow:0 1px 2px rgba(0,0,0,.95);';
    root.dataset.testid = 'loot-label';
    const box = document.createElement('div');
    box.style.cssText = 'flex:0 0 auto;width:18px;height:18px;display:grid;place-items:center;';
    const img = document.createElement('img');
    img.style.cssText = 'width:18px;height:18px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,.7));';
    img.alt = ''; img.decoding = 'async'; img.draggable = false;
    box.appendChild(img);
    const name = document.createElement('span');
    name.style.cssText = 'min-width:0;overflow:hidden;text-overflow:ellipsis;';
    name.dataset.testid = 'loot-label-name';
    const tag = document.createElement('span');
    tag.style.cssText = 'flex:0 0 auto;font:900 9px system-ui,"Malgun Gothic",sans-serif;padding:0 3px;border-radius:4px;border:1px solid currentColor;line-height:12px;background:rgba(0,0,0,.35);';
    tag.dataset.testid = 'loot-label-tag';
    root.append(box, name, tag);
    this.layer.appendChild(root);
    const e: Entry = { root, box, img, name, tag, pos: new TagPosCache(), slot: -1, item: null, kind: -1, grade: -1, aux: -1, tier: -1, icons: [], iconTry: 0, w: 120, shown: false };
    img.addEventListener('error', () => {
      if (e.iconTry + 1 < e.icons.length) { e.iconTry++; img.src = assetUrl(e.icons[e.iconTry]); } else box.style.display = 'none';
    });
    return e;
  }

  /** 던전이 아니거나 드롭이 없으면 전부 숨긴다. width·height = 화면 픽셀, panels = shared/panelRects 예약 사각형(렌더가 프레임당 한 번 읽어 넘긴다), nowMs = 실시간 */
  update(view: GameView, camera: THREE.Camera, width: number, height: number, panels: readonly ScreenRect[] = NO_RECTS, nowMs = 0): void {
    const l = view.loot;
    if (!combatScene(view.flow.scene) || view.flow.paused) { this.hideAll(); return; }
    const hx = view.hero.x, hz = view.hero.z;
    const max = LOOT_LABEL.max, near2 = LOOT_LABEL.near * LOOT_LABEL.near, big2 = LOOT_LABEL.bigNear * LOOT_LABEL.bigNear, far2 = LOOT_LABEL.freshFar * LOOT_LABEL.freshFar;
    let n = 0;
    for (let i = 0; i < l.capacity; i++) {
      if (!l.active[i]) continue;
      const dx = l.x[i] - hx, dz = l.z[i] - hz, d2 = dx * dx + dz * dz;
      const g = l.kind[i] === 0 ? Math.min(4, l.grade[i]) : 0;
      const fresh = l.age[i] < LOOT_LABEL.freshTicks && d2 <= far2;
      if (!(d2 <= near2 || fresh || (g >= 3 && d2 <= big2))) continue;
      let score = d2 - GRADE_BONUS[g];
      for (let j = 0; j < max; j++) if (this.entries[j].slot === i) { score -= HOLD_BONUS; break; }
      // 점수가 작은 순으로 max개만 남기는 고정 크기 삽입(할당 없음)
      let p = n < max ? n : max;
      if (p === max && score >= this.selScore[max - 1]) continue;
      if (n < max) n++;
      p = n - 1;
      while (p > 0 && this.selScore[p - 1] > score) { this.selScore[p] = this.selScore[p - 1]; this.selIdx[p] = this.selIdx[p - 1]; p--; }
      this.selScore[p] = score; this.selIdx[p] = i;
    }
    if (n === 0) { this.hideAll(); return; }
    this.layer.style.display = 'block'; this.any = true;
    // 이미 그 드롭을 가리키는 이름표는 그대로, 아닌 것은 비어 있거나 더 이상 뽑히지 않은 이름표에 새로 배정한다
    for (let e = 0; e < max; e++) {
      const en = this.entries[e];
      let keep = false;
      for (let k = 0; k < n; k++) if (this.selIdx[k] === en.slot) { keep = true; break; }
      if (!keep) en.slot = -1;
    }
    const k0 = Math.max(0.78, Math.min(1.1, height / 640));
    let placed = 0;
    for (let k = 0; k < n; k++) {
      const i = this.selIdx[k];
      let e = -1;
      for (let j = 0; j < max; j++) if (this.entries[j].slot === i) { e = j; break; }
      if (e < 0) { for (let j = 0; j < max; j++) if (this.entries[j].slot < 0) { e = j; break; } }
      if (e < 0) continue;
      const en = this.entries[e];
      en.slot = i;
      this.sync(en, l, i);
      // 화면 좌표: 드롭 위쪽(기둥 아랫부분)에 달고 머리 위로 띄운다
      const sx = this.project(camera, l.x[i], 0.75, l.z[i], width, height);
      if (Number.isNaN(sx)) continue;
      // 화면 아래쪽 HUD(스킬 바·조이스틱) 위까지만: 더 아래에 있는 드롭의 이름표는 그 선 위로 올려 붙인다
      this.sx[placed] = sx; this.sy[placed] = Math.min(this.projY, height * 0.86); this.ent[placed] = e; placed++;
    }
    // 겹침 정리: 앞(가까운) 이름표가 제자리, 뒤 이름표가 겹치면 위로 민다
    const rowH = LOOT_LABEL.rowH * k0;
    for (let a = 1; a < placed; a++) {
      for (let tries = 0; tries < 6; tries++) {
        let hit = false;
        for (let b = 0; b < a; b++) {
          const wa = this.entries[this.ent[a]].w * k0, wb = this.entries[this.ent[b]].w * k0;
          if (Math.abs(this.sx[a] - this.sx[b]) < (wa + wb) * 0.5 && Math.abs(this.sy[a] - this.sy[b]) < rowH) { this.sy[a] = this.sy[b] - rowH - 1; hit = true; }
        }
        if (!hit) break;
      }
    }
    // 화면 위쪽 끝(HUD 띠) 아래로는 올라가지 않는다
    const topMin = Math.max(30 * k0, TOP_KEEP);
    const hudRects = this.hudAvoid.update(nowMs, width, height);
    for (let e = 0; e < max; e++) this.entries[e].shown = false;
    for (let a = 0; a < placed; a++) {
      const en = this.entries[this.ent[a]];
      en.shown = this.sy[a] >= topMin;
      if (!en.shown) continue;
      // HUD 패널·조작 영역 밑이면 옆으로 비킨다(가로 이동만, 화면 안에서). 그려지는 상자 = transform translate(-50%,-100%) 뒤 scale(k0)(원점 왼쪽 위):
      // 왼쪽 위 (x − w/2, y − h), 크기 w·k0 × h·k0. 비켜도 여전히 HUD와 겹치면(자리가 없음) 이 이름표는 접는다(빛기둥은 그대로 — 글자가 HUD 밑에 깔리지 않게)
      const bw = en.w * k0, bh = LOOT_LABEL.rowH * k0;
      const bx0 = this.sx[a] - en.w * 0.5, by0 = this.sy[a] - LOOT_LABEL.rowH;
      let dx = 0;
      if (panels.length) dx = avoidShift(bx0, bx0 + bw, by0, by0 + bh, width, panels);
      if (hudRects.length) dx += avoidShift(bx0 + dx, bx0 + bw + dx, by0, by0 + bh, width, hudRects);
      if (overlaps(bx0 + dx, bx0 + bw + dx, by0, by0 + bh, panels) || overlaps(bx0 + dx, bx0 + bw + dx, by0, by0 + bh, hudRects)) { en.shown = false; continue; }
      const x = Math.round(this.sx[a] + dx), y = Math.round(this.sy[a]);
      if (en.root.style.display !== 'flex') en.root.style.display = 'flex';
      if (en.pos.update(x, y)) en.root.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-100%) scale(' + k0.toFixed(2) + ')';
    }
    let shown = 0;
    for (let e = 0; e < max; e++) {
      const en = this.entries[e];
      if (en.shown) shown++;
      else if (en.root.style.display !== 'none') { en.root.style.display = 'none'; if (en.slot < 0) { en.kind = -1; en.item = null; } }
    }
    this.shownCount = shown;
  }

  private projY = 0;
  /** 월드 점 → 화면 x(px), y는 projY. 카메라 뒤·화면 밖이면 NaN */
  private project(camera: THREE.Camera, x: number, y: number, z: number, width: number, height: number): number {
    const m = camera.matrixWorldInverse.elements, p = camera.projectionMatrix.elements;
    // 뷰 행렬 × 투영 행렬을 점에 직접 적용한다(Vector3 할당 없음)
    const vx = m[0] * x + m[4] * y + m[8] * z + m[12];
    const vy = m[1] * x + m[5] * y + m[9] * z + m[13];
    const vz = m[2] * x + m[6] * y + m[10] * z + m[14];
    const cw = p[3] * vx + p[7] * vy + p[11] * vz + p[15];
    if (cw <= 0) return Number.NaN;
    const nx = (p[0] * vx + p[4] * vy + p[8] * vz + p[12]) / cw;
    const ny = (p[1] * vx + p[5] * vy + p[9] * vz + p[13]) / cw;
    const px = (nx * 0.5 + 0.5) * width, py = (-ny * 0.5 + 0.5) * height;
    if (px < -60 || px > width + 60 || py < -40 || py > height + 40) return Number.NaN;
    this.projY = py;
    return px;
  }

  /** 드롭 내용이 바뀌었을 때만 이름·그림·태그를 다시 쓴다 */
  private sync(en: Entry, l: GameView['loot'], i: number): void {
    const kind = l.kind[i], grade = l.grade[i], aux = l.aux[i], tier = l.tier[i], item = l.item[i] ?? null;
    if (en.kind === kind && en.grade === grade && en.aux === aux && en.tier === tier && en.item === item) return;
    en.kind = kind; en.grade = grade; en.aux = aux; en.tier = tier; en.item = item;
    const info: DropInfo = kind === 0 ? gearDropInfo(item, grade, aux) : kind === 1 ? stoneDropInfo(tier, aux) : gemDropInfo(aux, grade);
    en.name.textContent = info.name;
    en.name.style.color = info.color === '#e8e8e8' ? '#fff' : info.color;
    en.tag.textContent = info.tag;
    en.tag.style.color = KIND_TAG_COLOR[kind] ?? '#fff';
    en.root.style.borderColor = info.color;
    en.root.style.boxShadow = kind === 0 && (grade >= 3 || !!item?.nu) ? '0 0 10px -1px ' + info.color : 'none';
    en.icons = info.icons.length ? info.icons : [KIND_ICON[kind] ?? KIND_ICON[0]];
    en.iconTry = 0;
    en.box.style.display = 'grid';
    en.img.src = assetUrl(en.icons[0]);
    // 폭: 글자 폭을 내용 키별로 한 번만 재서 쓴다(offsetWidth 읽기는 강제 레이아웃이라 쓰지 않는다 — PR #229 리뷰 P2-3)
    en.w = labelWidth(info.name, info.tag);
    en.pos.update(Number.NaN, Number.NaN); // 다음 프레임에 위치를 다시 쓴다
  }

  private hideAll(): void {
    this.shownCount = 0;
    if (!this.any) return;
    this.any = false;
    this.layer.style.display = 'none';
    for (const en of this.entries) { en.slot = -1; en.kind = -1; en.item = null; en.shown = false; en.root.style.display = 'none'; }
  }

  clear(): void { this.hideAll(); }

  dispose(): void { this.layer.remove(); }
}
